/**
 * Audio recorder worklet (16kHz mono mic capture -> 16-bit PCM chunks).
 *
 * The processor is referenced with `new URL(..., import.meta.url)` so Vite
 * serves it in dev and emits it as a hashed asset in the build.
 */
export async function startAudioRecorderWorklet(
  audioRecorderHandler: (pcm: ArrayBuffer) => void,
): Promise<[AudioWorkletNode, AudioContext, MediaStream]> {
  const audioRecorderContext = new AudioContext({ sampleRate: 16000 });

  const workletURL = new URL("./pcm-recorder-processor.js", import.meta.url);
  await audioRecorderContext.audioWorklet.addModule(workletURL);

  const micStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: { ideal: 1 },
      echoCancellation: { ideal: true },
      noiseSuppression: { ideal: true },
      autoGainControl: { ideal: true },
    },
  });
  const source = audioRecorderContext.createMediaStreamSource(micStream);

  const audioRecorderNode = new AudioWorkletNode(audioRecorderContext, "pcm-recorder-processor");
  source.connect(audioRecorderNode);
  audioRecorderNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
    audioRecorderHandler(convertFloat32ToPCM(event.data));
  };

  return [audioRecorderNode, audioRecorderContext, micStream];
}

export function stopMicrophone(micStream: MediaStream): void {
  micStream.getTracks().forEach((track) => track.stop());
}

// Convert Float32 samples in [-1, 1] to 16-bit PCM.
function convertFloat32ToPCM(inputData: Float32Array): ArrayBuffer {
  const pcm16 = new Int16Array(inputData.length);
  for (let i = 0; i < inputData.length; i++) {
    pcm16[i] = inputData[i] * 0x7fff;
  }
  return pcm16.buffer;
}
